import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, filter, finalize, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from "src/app/core/app.state";
import { CommonService } from "src/app/shared/common.service";
import { LocalStorageService } from "src/app/shared/localstorage.service";
import { AuthResponse } from "../models/auth-response";
import { AuthService } from "../services/auth.service";
import { LoginAction, LoginActionError, LoginActionSuccess, ForgetPasswordAction, SetAuthLoading, ResetPasswordAction, ForgetPasswordError, ResetPasswordError, MfaChallengeRequired, MfaVerifyAction, MfaSendEmailCode, MfaEmailCodeSent, MfaSendEmailCodeError, MfaChallengeExpired } from "./auth.actions";
import { MfaChallenge, MfaEmailSendResult, isMfaChallenge } from "../models/mfa-challenge";
import { AppError, ErrorCode } from "src/app/core/models/api-error";
import { APIResponse } from "src/app/core/models/api-response";
import { ToastMessages } from "src/app/shared/enums/shared.enums";
import { TranslateService } from "@ngx-translate/core";
import { setLanguageSuccess } from "src/app/features/settings/store/settings.actions";
import { LanguageService } from "src/app/config/language.service";
import { FeatureFlagService } from "src/app/core/services/feature-flag.service";
import { loadPermissionsFromLogin } from "src/app/core/store/permissions/permissions.actions";
import { loadHiddenFieldsFromLogin } from "src/app/core/store/field-visibility/field-visibility.actions";
import { MfaEnrolmentPromptService } from '../services/mfa-enrolment-prompt.service';
import { AuthReturnUrlService } from "src/app/core/services/auth-return-url.service";
import { ErrorMessageService } from "src/app/core/services/error-message.service";

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private authService: AuthService,
    private router: Router,
    private commonService: CommonService,
    private translateService: TranslateService,
    private languageService: LanguageService,
    private featureFlagService: FeatureFlagService,
    private returnUrl: AuthReturnUrlService,
    private errorMessages: ErrorMessageService,
    private enrolmentPrompt: MfaEnrolmentPromptService
  ) { }

  /** Orders list — see CLAUDE.md: the orders table is the quotations component. */
  private static readonly DEFAULT_LANDING_URL = '/quotations';

  /**
   * The only place a session becomes real. Three effects hang off
   * LoginActionSuccess, so a completion path that skips this presents as
   * "the sidebar is empty".
   */
  private finishLogin(payload: APIResponse<AuthResponse>) {
    const session = payload.object;
    // A 200 with no session is a server fault: storing it would leave a
    // token-less currentUser and navigate into a dead session.
    if (!session?.access_token) {
      return LoginActionError({
        error: {
          messages: this.translateService.instant('common.toaster.server_error'),
          code: ErrorCode.INTERNAL_ERROR,
          // The transport did say 200; the body is what was wrong.
          status: 200,
        },
      });
    }
    const currentLang = session?.user?.lang;
    if (currentLang) {
      this.languageService.setUserLanguage(currentLang.split('_')[0]);
    }
    LocalStorageService.put('currentUser', session as unknown as { [key: string]: any });
    // navigateByUrl, not navigate([...]): the captured deep link is a
    // serialized URL and carries its own query string.
    this.router.navigateByUrl(
      this.returnUrl.consume() ?? AuthEffects.DEFAULT_LANDING_URL
    );
    this.store.dispatch(SetAuthLoading({ isLoading: false }));
    return LoginActionSuccess({ payload });
  }

  /** Localizes the codes we own; the rest keep the server's message. */
  private parseAuthError(error: unknown): AppError {
    const appError = this.errorMessages.parse(error as never);
    const key = AuthEffects.LOGIN_ERROR_KEYS[appError.code as string];
    if (!key) { return appError; }
    const translated = this.translateService.instant(key);
    // instant() echoes the key when missing -- better the server's message
    // than a raw i18n path.
    return translated && translated !== key
      ? { ...appError, messages: translated }
      : appError;
  }

  private static readonly LOGIN_ERROR_KEYS: Record<string, string> = {
    [ErrorCode.MFA_INVALID_CODE]: 'authentication.login_error.mfa_invalid_code',
    [ErrorCode.MFA_ENROLLMENT_REQUIRED]: 'authentication.login_error.mfa_enrollment_required',
    [ErrorCode.RATE_LIMITED]: 'authentication.login_error.rate_limited',
  };

  LoginEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginAction),
      switchMap((action) =>
        this.authService.login(action.payload).pipe(
          // Two discriminated 200 bodies. A challenge must not become
          // LoginActionSuccess: that would store a token-less currentUser,
          // navigate away, and burn the captured deep link.
          map((payload: APIResponse<AuthResponse | MfaChallenge>) =>
            isMfaChallenge(payload.object)
              ? MfaChallengeRequired({ challenge: payload.object })
              : this.finishLogin(payload as APIResponse<AuthResponse>)
          ),
          // Parsed here rather than in the reducer: ErrorMessageService is the
          // one place that knows the backend's envelopes, and the store must
          // hold something already fit to show.
          catchError((error) =>
            of(LoginActionError({ error: this.parseAuthError(error) }))
          ),
          finalize(() => {
            this.store.dispatch(SetAuthLoading({ isLoading: false }))
          })
        )
      ),
      finalize(() => { })
    )
  )

  MfaVerifyEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MfaVerifyAction),
      withLatestFrom(this.store.select((state) => state.auth.challenge)),
      // A code with no challenge behind it is a race, not a request.
      filter(([, challenge]) => !!challenge),
      switchMap(([action, challenge]) =>
        this.authService.verifyMfa(challenge!.mfa_token, action.code).pipe(
          map((payload) => this.finishLogin(payload)),
          catchError((error) =>
            of(LoginActionError({ error: this.parseAuthError(error) }))
          ),
          finalize(() => {
            this.store.dispatch(SetAuthLoading({ isLoading: false }))
          })
        )
      )
    )
  )

  MfaSendEmailCodeEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MfaSendEmailCode),
      withLatestFrom(this.store.select((state) => state.auth.challenge)),
      filter(([, challenge]) => !!challenge),
      switchMap(([, challenge]) =>
        this.authService.sendChallengeEmailCode(challenge!.mfa_token).pipe(
          map((response: APIResponse<MfaEmailSendResult>) =>
            MfaEmailCodeSent({
              nextAllowedAt: response.object?.next_allowed_at ?? '',
              maskedEmail: response.object?.masked_email ?? '',
            })
          ),
          // Its own action: a throttled resend is not a refused code, and
          // LoginActionError clears the field.
          catchError((error) =>
            of(MfaSendEmailCodeError({ error: this.parseAuthError(error) }))
          )
        )
      )
    )
  )

  /** Says why the challenge vanished; the banner renders messages, not keys,
   *  and only this class holds the translator. */
  MfaChallengeExpiredEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MfaChallengeExpired),
      map(() =>
        LoginActionError({
          error: {
            messages: this.translateService.instant(
              'authentication.login_error.mfa_challenge_expired'
            ),
            code: ErrorCode.MFA_REQUIRED,
            status: 401,
          },
        })
      )
    )
  )

  RefreshFeaturesOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActionSuccess),
      switchMap(() =>
        this.featureFlagService.loadFeatures(true).pipe(catchError(() => of([])))
      )
    ),
    { dispatch: false }
  );

  /**
   * Offer a second factor to whoever has none. `dispatch: false` and behind a
   * status read of its own: nothing about landing in the app waits on it, and
   * a failed read means no prompt rather than a session stuck outside.
   */
  PromptMfaEnrolmentOnLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoginActionSuccess),
        tap(() => this.enrolmentPrompt.maybePrompt())
      ),
    { dispatch: false }
  );

  HydratePermissionsOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActionSuccess),
      map(({ payload }) =>
        loadPermissionsFromLogin({
          permissions: payload.object?.user?.permissions ?? [],
        })
      )
    )
  );

  HydrateHiddenFieldsOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActionSuccess),
      map(({ payload }) =>
        loadHiddenFieldsFromLogin({
          hiddenFields: payload.object?.user?.hidden_fields ?? {},
        })
      )
    )
  );

  ForgetPasswordEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForgetPasswordAction),
      switchMap((action) =>
        this.authService.forgetPassword(action.payload).pipe(
          map((response: APIResponse<void>) => {
            this.commonService.showToast(
              response.message,
              'SUCCESS'
            );
            this.store.dispatch(SetAuthLoading({ isLoading: false }))
            return { type: '[Auth] Forget Password Success' };
          }),
          catchError((error) =>
            of(
              ForgetPasswordError({ error: error.message || 'An error occurred' })
            ).pipe(
              tap(() => {
                this.store.dispatch(SetAuthLoading({ isLoading: false }))
              }
              )
            )
          )
        )
      )
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetPasswordAction),
      switchMap((action) =>
        this.authService.resetPassword(
          action.payload.newPassword,
          action.payload.confirm_password,
          action.payload.token
        ).pipe(
          map(() => {
            this.commonService.showToast(
              this.translateService.instant(`auth_toast.${ToastMessages.PasswordResetSuccess}`),
              'SUCCESS'
            );
            return SetAuthLoading({ isLoading: false });
          }),
          tap(() => {
            this.store.dispatch(SetAuthLoading({ isLoading: false }))
            this.router.navigate(['']);
          }),
          catchError((error) =>
            of(
              ResetPasswordError({ error: error.message || 'An error occurred' })
            ).pipe(
              tap(() => {
                this.store.dispatch(SetAuthLoading({ isLoading: false }))
              }
              )
            )
          )
        )
      )
    )
  );


};
